/** 写真の取り込みパイプライン（仕様書 §9）。 */

import exifr from 'exifr';
import { PHOTO_JPEG_QUALITY, PHOTO_MAX_EDGE } from '../domain/types';

const MAX_INPUT_BYTES = 50 * 1024 * 1024;

export const ACCEPTED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/gif',
];

export class PhotoError extends Error {
  readonly fileName: string;

  constructor(fileName: string, message: string) {
    super(message);
    this.name = 'PhotoError';
    this.fileName = fileName;
  }
}

export interface ProcessedPhoto {
  blob: Blob;
  width: number;
  height: number;
  /** EXIF から読み取れた位置情報（§11.1 で使う）。 */
  gps: { lat: number; lng: number } | null;
}

/** EXIF の GPS を読む。読めなくても取り込みは続行する。 */
async function readGps(file: File): Promise<{ lat: number; lng: number } | null> {
  try {
    const gps = await exifr.gps(file);
    if (gps && Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude)) {
      return { lat: gps.latitude, lng: gps.longitude };
    }
  } catch {
    /* EXIF が無い・壊れている場合は無視する */
  }
  return null;
}

/**
 * 段階的に縮小する。
 * 1/2 未満へ一度に縮小するとモアレやジャギーが出るため、1/2 ずつ落としてから最終サイズにする。
 */
function downscale(src: CanvasImageSource, sw: number, sh: number, tw: number, th: number): HTMLCanvasElement {
  let cw = sw;
  let ch = sh;
  let canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  let ctx = canvas.getContext('2d')!;
  ctx.drawImage(src, 0, 0, cw, ch);

  while (cw / 2 > tw) {
    const nw = Math.max(tw, Math.round(cw / 2));
    const nh = Math.max(th, Math.round(ch / 2));
    const next = document.createElement('canvas');
    next.width = nw;
    next.height = nh;
    const nctx = next.getContext('2d')!;
    nctx.imageSmoothingQuality = 'high';
    nctx.drawImage(canvas, 0, 0, nw, nh);
    canvas = next;
    ctx = nctx;
    cw = nw;
    ch = nh;
  }

  if (cw !== tw || ch !== th) {
    const out = document.createElement('canvas');
    out.width = tw;
    out.height = th;
    const octx = out.getContext('2d')!;
    octx.imageSmoothingQuality = 'high';
    octx.drawImage(canvas, 0, 0, tw, th);
    return out;
  }
  return canvas;
}

/**
 * 1枚の画像ファイルを取り込む。
 * EXIF Orientation を適用し、長辺 1024px を超える場合のみ縮小し、JPEG 0.85 で書き出す。
 * 1024px 以下の画像は拡大しない。
 */
export async function processPhoto(file: File): Promise<ProcessedPhoto> {
  if (file.size > MAX_INPUT_BYTES) {
    throw new PhotoError(file.name, 'ファイルが大きすぎます（50MBまで）。');
  }

  const gps = await readGps(file);

  let bitmap: ImageBitmap;
  try {
    // EXIF Orientation を反映したビットマップを得る
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new PhotoError(
      file.name,
      'この形式の画像は、この端末では読み込めません。JPEGでお試しください。',
    );
  }

  const { width: sw, height: sh } = bitmap;
  const long = Math.max(sw, sh);
  const scale = long > PHOTO_MAX_EDGE ? PHOTO_MAX_EDGE / long : 1;
  const tw = Math.max(1, Math.round(sw * scale));
  const th = Math.max(1, Math.round(sh * scale));

  const canvas =
    scale === 1
      ? (() => {
          const c = document.createElement('canvas');
          c.width = tw;
          c.height = th;
          // 透過画像は白で合成する（出力はJPEGのため）
          const ctx = c.getContext('2d')!;
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, tw, th);
          ctx.drawImage(bitmap, 0, 0);
          return c;
        })()
      : (() => {
          const scaled = downscale(bitmap, sw, sh, tw, th);
          const c = document.createElement('canvas');
          c.width = tw;
          c.height = th;
          const ctx = c.getContext('2d')!;
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, tw, th);
          ctx.drawImage(scaled, 0, 0);
          return c;
        })();

  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', PHOTO_JPEG_QUALITY),
  );
  if (!blob) throw new PhotoError(file.name, '画像の変換に失敗しました。');

  return { blob, width: tw, height: th, gps };
}

/** 複数ファイルを順に処理する。失敗した1枚はスキップし、他は続行する。 */
export async function processPhotos(
  files: File[],
  onError: (e: PhotoError) => void,
): Promise<ProcessedPhoto[]> {
  const out: ProcessedPhoto[] = [];
  for (const f of files) {
    try {
      out.push(await processPhoto(f));
    } catch (e) {
      onError(e instanceof PhotoError ? e : new PhotoError(f.name, '取り込みに失敗しました。'));
    }
  }
  return out;
}
